import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useDealsData = (searchTerm: string, countryFilter: string, priceFilter: string) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<string[]>([]);

  const sortVacationsByPriceAvailability = (vacations: any[]) => {
    return vacations.sort((a, b) => {
      const aHasPrice = a.price !== null && a.price !== undefined && a.price > 0;
      const bHasPrice = b.price !== null && b.price !== undefined && b.price > 0;
      
      // Vacations with known prices come first
      if (aHasPrice && !bHasPrice) return -1;
      if (!aHasPrice && bHasPrice) return 1;
      
      // If both have prices, sort by price (ascending for deals)
      if (aHasPrice && bHasPrice) return a.price - b.price;
      
      return 0;
    });
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('Complete feed')
        .select('country')
        .not('country', 'is', null)
        .not('imageURL', 'is', null)
        .not('name', 'is', null);
      
      if (error) throw error;
      
      const uniqueCountries = [...new Set(data.map(item => item.country))].sort();
      setCountries(uniqueCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    }
  };

  const buildQuery = (baseQuery: any) => {
    let query = baseQuery;

    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
    }

    if (countryFilter !== "all") {
      query = query.eq('country', countryFilter);
    }

    if (priceFilter === 'onbekend') {
      query = query.or('price.is.null,price.eq.0');
    } else if (priceFilter === '500') {
      query = query.gt('price', 0).lt('price', 500);
    } else if (priceFilter === '1000') {
      query = query.gte('price', 500).lt('price', 1000);
    } else if (priceFilter === '2000') {
      query = query.gte('price', 1000).lt('price', 2000);
    } else if (priceFilter === '3500') {
      query = query.gte('price', 2000).lt('price', 3500);
    } else if (priceFilter === '5000') {
      query = query.gte('price', 3500).lt('price', 5000);
    } else if (priceFilter === '5000+') {
      query = query.gte('price', 5000);
    }

    return query;
  };

  const fetchDeals = async () => {
    setLoading(true);
    try {
      let baseQuery = supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null);

      // For deals, we want either low priced items OR items with unknown prices
      if (priceFilter === 'onbekend') {
        baseQuery = baseQuery.or('price.is.null,price.eq.0');
      } else {
        baseQuery = baseQuery.or('price.lt.500,price.is.null,price.eq.0');
      }

      baseQuery = baseQuery.order('price', { ascending: true, nullsFirst: false });

      let query = buildQuery(baseQuery);
      const { data, error } = await query.limit(100);
      
      if (error) throw error;
      
      // Apply sorting with price preference
      const sortedData = sortVacationsByPriceAvailability(data || []);
      
      console.log('Deals data loaded from Complete feed with price preference:', sortedData?.length || 0, 'items');
      setVacations(sortedData);
      
    } catch (error: any) {
      toast.error("Fout bij het laden van deals");
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    fetchCountries();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchDeals();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, countryFilter, priceFilter]);

  return {
    vacations,
    loading,
    countries
  };
};
